/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { skipToken } from '@reduxjs/toolkit/query/react'
import Button from 'components/atoms/Button'
import DataTable from 'components/atoms/DataTable'
import { Popover } from 'components/atoms/Popover'
import SearchableInput from 'components/atoms/SearchableInput'
import { getAuthorizationExpirationRowCss } from 'helpers/orderRowHighlight'
import { fullName, patientFormalName } from 'helpers/nameHelper'

import { TableWrapper } from 'components/atoms/TableWrapper'
import Toast from 'components/atoms/Toast'
import PatientUpsert from 'components/dme/orderEdit/views/forms/PatientUpsert'
import { PatientPreferredDme } from 'components/lab/patient/PatientPreferredDme'
import { BillingChargesListing } from 'components/lab/billingCharge/BillingChargesListing'
import { QuickChargeModal } from 'components/lab/billingCharge/QuickChargeModal'
import PatientTransactionsListing from 'components/lab/billingLedger/PatientTransactionsListing'
import { PatientStatementsListing } from 'components/lab/billingPatientStatements/PatientStatementsListing'
import { StatementRemindersListing } from 'components/lab/billingPatientStatements/StatementRemindersListing'
import { PaymentReceiptsListing } from 'components/lab/billingPaymentReceipts/PaymentReceiptsListing'
import { BillingPaymentsListing } from 'components/lab/billingPayment/BillingPaymentsListing'
import { PatientFinancialSummary } from 'components/lab/patient/sections/PatientFinancialSummary'
import { PatientHeaderInfo } from 'components/lab/patient/sections/PatientHeaderInfo'
import React, { useState } from 'react'
import { Button as BsButton, Modal } from 'react-bootstrap'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useGetLabOrdersQuery } from 'store/services/labOrder'
import { useGetLabPatientQuery, useMovePatientMutation, useUpdatePatientTagMutation } from 'store/services/patient'
import { useGetLabOfficesQuery } from 'store/services/office'
import { useGetTagQuery, useGetTagsQuery } from 'store/services/tags'
import {
  useGeneratePatientStatementMutation,
  useLazyGetStatementDownloadQuery,
} from 'store/services/patientStatements'
import {
  useGeneratePaymentReceiptMutation,
  useLazyGetPaymentReceiptDownloadQuery,
} from 'store/services/paymentReceipts'
import { BillingPatientStatementDeliveryMethod } from 'store/types'
import ReactSelect from 'react-select'
import { useDebounce } from 'use-debounce'
import DashboardContent from '../atoms/DashboardContent'
import { DeviceComplaintsList } from 'components/organisms/deviceComplaints/DeviceComplaintsList'
import { Can } from '../Can'
import routes from '../constants/routes'
import OrderTableColumns from '../order/OrderTableColumns'
import { UserPermission } from '../user/permissions'

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.2rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

export enum PatientSection {
  Financial = 'financial',
  Orders = 'orders',
  EditPatient = 'edit-patient',
  Ledger = 'ledger',
  Charges = 'charges',
  Payments = 'payments',
  Statements = 'statements',
  Reminders = 'reminders',
  Receipts = 'receipts',
}

const PatientDetails = () => {
  const { patientId } = useParams()
  const navigate = useNavigate()
  const [patientFormDirty, setPatientFormDirty] = useState(false)
  const { data: patient, isLoading: patientLoading } = useGetLabPatientQuery(
    patientId ? { id: Number(patientId) } : skipToken,
  )
  const { data: orders, isLoading: ordersLoading } = useGetLabOrdersQuery(
    patient
      ? {
          patientId: patient.id,
        }
      : skipToken,
  )
  const [mainSection, setMainSection] = useState<PatientSection>(PatientSection.Financial)
  const [searchParams] = useSearchParams()
  const [financialSection, setFinancialSection] = useState<PatientSection>(
    () =>
      Object.values(PatientSection).find((section) => section === searchParams.get('section')) ??
      PatientSection.Financial,
  )
  const [showQuickCharge, setShowQuickCharge] = useState(false)
  const [open, setOpen] = useState(false)
  const [getDownloadUrl] = useLazyGetStatementDownloadQuery()
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [showTagModal, setShowTagModal] = useState(false)
  const [selectedTagText, setSelectedTagText] = useState('')
  const [selectedOffice, setSelectedOffice] = useState<{ value: number; label: string } | null>(null)
  const [officeSearch, setOfficeSearch] = useState('')
  const [debouncedOfficeSearch] = useDebounce(officeSearch, 300)
  const { data: offices, isFetching: officesLoading } = useGetLabOfficesQuery(
    showMoveModal ? { name: debouncedOfficeSearch } : skipToken,
  )
  const [movePatient, { isLoading: movePatientLoading }] = useMovePatientMutation()
  const [updatePatientTag, { isLoading: updatePatientTagLoading }] = useUpdatePatientTagMutation()
  const { data: tags = [] } = useGetTagsQuery()
  const { data: currentTag } = useGetTagQuery(
    patient?.tagId ? { id: patient.tagId, includeDeleted: true } : skipToken,
  )
  const activeTag = patient?.tagId ? currentTag : undefined
  const officeOptions = React.useMemo(
    () =>
      (offices ?? []).map((o) => ({
        value: o.id,
        label: `${o.officeName} (${o.addressCity}, ${o.addressState})`,
      })),
    [offices],
  )
  const tagOptions = React.useMemo(() => {
    if (!currentTag || tags.some((tag) => tag.id === currentTag.id)) {
      return tags
    }

    return [currentTag, ...tags]
  }, [currentTag, tags])
  const tagSearchOptions = React.useMemo(
    () => tagOptions.map((tag) => ({ label: tag.text, value: tag.text })),
    [tagOptions],
  )

  const [generateStatement, { isLoading: isGeneratingStatement }] =
    useGeneratePatientStatementMutation()
  const [selectedDelivery, setSelectedDelivery] =
    useState<BillingPatientStatementDeliveryMethod | null>(null)

  const deliveryOptions: {
    value: BillingPatientStatementDeliveryMethod
    label: string
    sendNow: boolean
  }[] = [
    { value: BillingPatientStatementDeliveryMethod.SMS, label: 'Send via SMS', sendNow: true },
    { value: BillingPatientStatementDeliveryMethod.EMAIL, label: 'Send via Email', sendNow: true },
    { value: BillingPatientStatementDeliveryMethod.PRINT, label: 'Print now', sendNow: false },
  ]

  const [generateReceipt, { isLoading: isGeneratingReceipt }] = useGeneratePaymentReceiptMutation()
  const [getReceiptDownloadUrl] = useLazyGetPaymentReceiptDownloadQuery()

  // Account-wide receipt: every charge on the patient. A per-payment receipt is
  // generated from the payment row itself.
  const handleGenerateReceipt = async () => {
    if (!patientId) {
      return
    }

    try {
      const receipt = await generateReceipt({ patientId: Number(patientId) }).unwrap()
      const link = await getReceiptDownloadUrl({
        receiptId: receipt.id,
        patientId: Number(patientId),
      }).unwrap()

      if (link?.url) {
        window.open(link.url, '_blank', 'noopener,noreferrer')?.focus()
      }

      // Account receipts are always rendered fresh, but the guard keeps the
      // toast honest if account-level reuse is ever added.
      if (!receipt.reused) {
        Toast({ type: 'success', label: 'Receipt generated' })
      }

      setFinancialSection(PatientSection.Receipts)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to generate receipt'
      Toast({ type: 'error', label: message })
    }
  }

  const handleGenerateStatement = async () => {
    if (!selectedDelivery) {
      return
    }

    try {
      const selected = deliveryOptions.find((o) => o.value === selectedDelivery)

      if (!patientId) {
        return
      }

      const response = await generateStatement({
        patientId: Number(patientId),
        deliveryMethods:
          selectedDelivery !== BillingPatientStatementDeliveryMethod.PRINT
            ? [selectedDelivery]
            : [],
        sendNow: selected?.sendNow,
      }).unwrap()

      if (selectedDelivery === BillingPatientStatementDeliveryMethod.PRINT) {
        if (!response.fileKey) {
          return
        }

        const link = await getDownloadUrl({ statementId: response.id, patientId: patient.id })

        if (link.data?.url) {
          const printWindow = window.open(link.data?.url, '_blank')
          printWindow?.focus()
        }
      }

      if (response.delivery && !response.delivery.sent.length) {
        // The statement exists; the message did not go. Say why, and keep it
        // on screen — a biller who misses this thinks the patient was told.
        const reasons = response.delivery.skipped
          .map((skip) => `${skip.method.toUpperCase()}: ${skip.reason}`)
          .join('; ')

        Toast({
          type: 'warning',
          label: `Statement generated but not sent — ${reasons}`,
          options: { autoClose: false },
        })
      } else {
        Toast({ type: 'success', label: 'Statement generated' })
      }

      setSelectedDelivery(null)
      setOpen(false)
    } catch (error) {
      if ((error as { data?: { message?: string } })?.data?.message) {
        const errorMessage = (error as { data: { message: string } }).data.message
        Toast({ type: 'error', label: errorMessage })
      }
    }
  }

  const handleMovePatient = async () => {
    if (!patientId || !selectedOffice) {
      return
    }
    try {
      await movePatient({ patientId: Number(patientId), officeId: selectedOffice.value }).unwrap()
      Toast({ type: 'success', label: 'Patient moved successfully' })
      setShowMoveModal(false)
      setSelectedOffice(null)
      setOfficeSearch('')
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message
      if (errorMessage) {
        Toast({ type: 'error', label: errorMessage })
      }
    }
  }

  const handleOpenTagModal = () => {
    setSelectedTagText(activeTag?.text || '')
    setShowTagModal(true)
  }

  const handleUpdatePatientTag = async () => {
    if (!patientId) {
      return
    }

    try {
      const trimmedTagText = selectedTagText.trim()
      const matchingTag = tagOptions.find((tag) => tag.text.toLowerCase() === trimmedTagText.toLowerCase())

      await updatePatientTag({
        patientId: Number(patientId),
        tagId: matchingTag?.id || null,
        tagText: trimmedTagText || null,
      }).unwrap()
      Toast({ type: 'success', label: 'Patient tag updated successfully' })
      setShowTagModal(false)
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message
      if (errorMessage) {
        Toast({ type: 'error', label: errorMessage })
      }
    }
  }

  const handleClearPatientTag = async () => {
    if (!patientId) {
      return
    }

    try {
      await updatePatientTag({ patientId: Number(patientId), tagId: null, tagText: null }).unwrap()
      Toast({ type: 'success', label: 'Patient tag cleared successfully' })
      setSelectedTagText('')
      setShowTagModal(false)
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message
      if (errorMessage) {
        Toast({ type: 'error', label: errorMessage })
      }
    }
  }

  return (
    <DashboardContent
      title="Patient Details"
      icon="fa fa-user"
      contentClassName={'p-0'}
      content={
        <>
          <div
            css={{
              background: '#ebecef',
            }}
            className={'pt-4 px-4 pb-0'}>
            <div
              className={'d-flex align-items-center justify-content-between pb-3'}
              css={{
                borderBottom:
                  mainSection !== PatientSection.EditPatient ? '1px solid #c4c8cf' : 'none',
              }}>
              <div
                css={{
                  fontSize: 22,
                  fontWeight: 600,
              }}>
                {patientFormalName(patient?.firstName, patient?.lastName)}
              </div>
              <div className="d-flex align-items-center">
                {mainSection === PatientSection.EditPatient ? (
                  <>
                    <BsButton
                      variant={'outline-dark'}
                      className={'mr-2'}
                      onClick={() => setMainSection(PatientSection.Financial)}>
                      Cancel
                    </BsButton>
                    <Button
                      className="btn btn-primary"
                      label="Save Changes"
                      type="submit"
                      form="patientUpsert"
                      disabled={!patientFormDirty}
                    />
                  </>
                ) : (
                  <>
                    <Can permission={UserPermission.MANAGE_TAGS}>
                      <BsButton
                        variant={'outline-primary'}
                        className="mr-2"
                        onClick={handleOpenTagModal}>
                        <i className="fas fa-flag mr-2" />
                        Tag Patient
                      </BsButton>
                    </Can>
                    <Can permission={UserPermission.MOVE_PATIENT}>
                      <BsButton
                        variant={'outline-primary'}
                        className="mr-2"
                        onClick={() => setShowMoveModal(true)}>
                        <i className="fas fa-random mr-2" />
                        Move Patient
                      </BsButton>
                    </Can>
                    <BsButton
                      variant={'outline-dark'}
                      onClick={() => setMainSection(PatientSection.EditPatient)}>
                      <i className="fas fa-edit mr-2" />
                      Edit Patient
                    </BsButton>
                  </>
                )}
              </div>
            </div>
            {!!activeTag?.text?.trim() && (
              <div
                className="mt-3 mb-2 px-3 py-2"
                css={{
                  background: '#fff4db',
                  border: '1px solid #f0d08a',
                  borderRadius: 3,
                  color: '#6a4b00',
                  fontSize: 14,
                  whiteSpace: 'pre-wrap',
                }}>
                <strong>Tag:</strong> {activeTag.text}
              </div>
            )}
            {mainSection !== PatientSection.EditPatient && (
              <PatientHeaderInfo
                patient={patient}
                mainSection={mainSection}
                setMainSection={setMainSection}
              />
            )}
          </div>
          {mainSection === PatientSection.Financial && (
            <div className={'p-4'}>
              <div
                className={'d-flex justify-content-between pb-4'}
                css={{
                  borderBottom: '1px solid #c4c8cf',
                }}>
                <div className={'d-flex'} css={{ gap: 5 }}>
                  <BsButton
                    variant={
                      financialSection === PatientSection.Financial ? 'dark' : 'outline-dark'
                    }
                    onClick={() => setFinancialSection(PatientSection.Financial)}>
                    Financial Summary
                  </BsButton>
                  <BsButton
                    variant={financialSection === PatientSection.Ledger ? 'dark' : 'outline-dark'}
                    onClick={() => setFinancialSection(PatientSection.Ledger)}>
                    Ledger
                  </BsButton>
                  <BsButton
                    variant={financialSection === PatientSection.Charges ? 'dark' : 'outline-dark'}
                    onClick={() => setFinancialSection(PatientSection.Charges)}>
                    Charges
                  </BsButton>
                  <BsButton
                    variant={financialSection === PatientSection.Payments ? 'dark' : 'outline-dark'}
                    onClick={() => setFinancialSection(PatientSection.Payments)}>
                    Active Charges / Payments
                  </BsButton>
                  <BsButton
                    variant={
                      financialSection === PatientSection.Statements ? 'dark' : 'outline-dark'
                    }
                    onClick={() => setFinancialSection(PatientSection.Statements)}>
                    Statement History
                  </BsButton>
                  <BsButton
                    variant={financialSection === PatientSection.Reminders ? 'dark' : 'outline-dark'}
                    onClick={() => setFinancialSection(PatientSection.Reminders)}>
                    Reminder History
                  </BsButton>
                  <BsButton
                    variant={financialSection === PatientSection.Receipts ? 'dark' : 'outline-dark'}
                    onClick={() => setFinancialSection(PatientSection.Receipts)}>
                    Receipts
                  </BsButton>
                </div>
                <div className={'d-flex'}>
                  <div
                    css={{
                      position: 'relative',
                    }}>
                    <BsButton
                      variant={'outline-secondary'}
                      className={'mr-2'}
                      disabled={!patientId || isGeneratingStatement}
                      onClick={() => {
                        setOpen(true)
                      }}>
                      Generate Statement
                    </BsButton>
                    <Popover setOpen={setOpen} open={open}>
                      <div>
                        <div css={{ fontWeight: 600 }}>Generate Statement</div>
                        <div
                          css={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.4rem',
                            margin: '10px 0',
                          }}>
                          {deliveryOptions.map((option) => (
                            <label
                              key={option.value}
                              css={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                margin: 0,
                              }}>
                              <input
                                type="radio"
                                name={`statement`}
                                checked={selectedDelivery === option.value}
                                onChange={() => setSelectedDelivery(option.value)}
                              />
                              <span css={{ fontWeight: 500 }}>{option.label}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          css={{
                            marginTop: '0.75rem',
                            width: '100%',
                            border: 'none',
                            borderRadius: 6,
                            padding: '0.5rem',
                            background: selectedDelivery ? '#007bff' : '#adb5bd',
                            color: '#fff',
                            cursor:
                              !selectedDelivery || isGeneratingStatement
                                ? 'not-allowed'
                                : 'pointer',
                            opacity: isGeneratingStatement ? 0.6 : 1,
                          }}
                          disabled={!selectedDelivery || isGeneratingStatement}
                          onClick={(evt) => {
                            evt.stopPropagation()
                            handleGenerateStatement()
                          }}>
                          Generate Statement
                        </button>
                      </div>
                    </Popover>
                  </div>
                  <BsButton
                    variant={'outline-secondary'}
                    className={'mr-2'}
                    disabled={!patientId || isGeneratingReceipt}
                    onClick={handleGenerateReceipt}>
                    Generate Receipt
                  </BsButton>
                  <Can permission={UserPermission.ADD_BILLING_CHARGES}>
                    <BsButton
                      variant={'secondary'}
                      className={'mr-2'}
                      onClick={() => {
                        navigate(
                          `${routes.index}${routes.patient.root}/${patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.new}`,
                        )
                      }}>
                      Create Charge
                    </BsButton>
                    <BsButton variant={'primary'} onClick={() => setShowQuickCharge(true)}>
                      Quick Charge
                    </BsButton>
                  </Can>
                </div>
              </div>
              {financialSection === 'financial' && patientId && (
                <PatientFinancialSummary patientId={Number(patientId)} />
              )}
              {financialSection === PatientSection.Ledger && (
                <div className="card card-primary mt-3">
                  <div className="row p-3">
                    <PatientTransactionsListing
                      patientId={patientId}
                      patientName={
                        patient
                          ? fullName(patient.firstName, patient.lastName)
                          : undefined
                      }
                    />
                  </div>
                </div>
              )}
              {financialSection === PatientSection.Charges && (
                <div className="card card-primary mt-3">
                  <div className="row p-3">
                    <BillingChargesListing patientId={patientId} bucket="all" />
                  </div>
                </div>
              )}
              {financialSection === PatientSection.Payments && (
                <div className="card card-primary mt-3">
                  <div className="row p-3">
                    <BillingPaymentsListing patientId={patientId} />
                  </div>
                </div>
              )}
              {financialSection === PatientSection.Statements && (
                <div className="card card-primary mt-3">
                  <div className="row p-3">
                    <div className="col-12">
                      {patientId && <PatientStatementsListing patientId={patientId} />}
                    </div>
                  </div>
                </div>
              )}
              {financialSection === PatientSection.Reminders && (
                <div className="card card-primary mt-3">
                  <div className="row p-3">
                    <div className="col-12">
                      {patientId && <StatementRemindersListing patientId={patientId} />}
                    </div>
                  </div>
                </div>
              )}
              {financialSection === PatientSection.Receipts && (
                <div className="card card-primary mt-3">
                  <div className="row p-3">
                    <div className="col-12">
                      {patientId && <PaymentReceiptsListing patientId={patientId} />}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {mainSection === PatientSection.EditPatient && (
            <div className="card card-primary mt-3">
              <div className="row p-3">
                <PatientUpsert
                  labUser
                  unlockedForm
                  modalView
                  patient={patient ?? {}}
                  setFormDirty={setPatientFormDirty}
                  extraFields={
                    <div className="col-6">
                      <PatientPreferredDme
                        patientId={Number(patientId)}
                        officeId={patient?.officeId}
                        preferredDmeOfficeId={patient?.preferredDmeOfficeId}
                      />
                    </div>
                  }
                />
              </div>
            </div>
          )}
          {mainSection === PatientSection.Orders && (
            <div className="card card-primary mt-3">
              <div className="row p-3">
                <StyledTableWrapper
                  className="card-body row"
                  style={{
                    overflowY:
                      !orders?.items?.length || orders?.items?.length < 10 ? 'unset' : 'auto',
                  }}>
                  <DataTable
                    data={orders}
                    getRowCss={getAuthorizationExpirationRowCss}
                    tableKey={`lab_patient_details_orders`}
                    status={patientLoading || ordersLoading}
                    tableColumns={[
                      ...OrderTableColumns({
                        testType: 'POXCOX',
                        bucket: 'ALL',
                      }),
                      {
                        Header: 'Test Type',
                        className: 'col-1',
                        accessor: 'testType',
                        Cell: ({ row }) => <span>{row.original.testType}</span>,
                      },
                    ]}
                    pageSize={30}
                    setCurrentPage={() => {}}
                    setSortOptions={() => {}}
                    onRowClick={(row) => {
                      navigate(`${routes.index}${routes.order.edit}${row.original.id}?bucket=ALL`)
                    }}
                  />
                </StyledTableWrapper>
              </div>
            </div>
          )}
          {mainSection === PatientSection.Orders && patientId && (
            <div className="card card-primary mt-3">
              <div className="card-body">
                <DeviceComplaintsList site="lab" patientId={Number(patientId)} canFile={false} />
              </div>
            </div>
          )}
          <Modal
            show={showTagModal}
            onHide={() => {
              setShowTagModal(false)
              setSelectedTagText(activeTag?.text || '')
            }}
            centered>
            <Modal.Header>
              <Modal.Title>Tag Patient</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="text-muted mb-3" style={{ fontSize: 14 }}>
                The selected tag will be visible on all patient orders.
              </div>
              <div className="mb-3">
                <strong>Current tag:</strong> {activeTag?.text || 'None'}
              </div>
              <label className="text-gray">Change Tag</label>
              <SearchableInput
                value={selectedTagText}
                options={tagSearchOptions}
                placeholder="Search or create a tag"
                onChange={setSelectedTagText}
                onBlur={() => {}}
              />
            </Modal.Body>
            <Modal.Footer>
              <BsButton
                variant="outline-secondary"
                onClick={() => {
                  setShowTagModal(false)
                  setSelectedTagText(activeTag?.text || '')
                }}>
                Cancel
              </BsButton>
              <BsButton
                variant="outline-danger"
                disabled={updatePatientTagLoading || !patient?.tagId}
                onClick={handleClearPatientTag}>
                Clear Tag
              </BsButton>
              <BsButton
                variant="primary"
                disabled={updatePatientTagLoading}
                onClick={handleUpdatePatientTag}>
                {updatePatientTagLoading ? 'Saving...' : 'Submit'}
              </BsButton>
            </Modal.Footer>
          </Modal>
          <Modal
            show={showMoveModal}
            onHide={() => {
              setShowMoveModal(false)
              setSelectedOffice(null)
              setOfficeSearch('')
            }}
            centered>
            <Modal.Header>
              <Modal.Title>Move Patient</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <label className="text-gray">Destination Office</label>
              <ReactSelect
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                className="basic-single w-100"
                classNamePrefix="select"
                isClearable
                isSearchable
                placeholder="Search office"
                name="officeId"
                options={officeOptions}
                value={selectedOffice}
                onChange={(item) => setSelectedOffice(item as { value: number; label: string } | null)}
                onInputChange={(value) => setOfficeSearch(value)}
                inputValue={officeSearch}
                isLoading={officesLoading}
              />
              <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
                All patient orders will be moved along with the patient.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <BsButton
                variant="outline-secondary"
                onClick={() => {
                  setShowMoveModal(false)
                  setSelectedOffice(null)
                  setOfficeSearch('')
                }}>
                Cancel
              </BsButton>
              <BsButton
                variant="primary"
                disabled={!selectedOffice || movePatientLoading}
                onClick={handleMovePatient}>
                {movePatientLoading ? 'Moving...' : 'Move Patient'}
              </BsButton>
            </Modal.Footer>
          </Modal>
          {showQuickCharge && (
            <QuickChargeModal
              open={showQuickCharge}
              onClose={() => setShowQuickCharge(false)}
              patientId={patientId as string}
            />
          )}
        </>
      }
      breadcrumb={[
        {
          label: 'Patient Directory',
          to: `${routes.index}${routes.patient.root}${routes.patient.directory}`,
        },
        {
          label: patientFormalName(patient?.firstName, patient?.lastName, patient?.middleName),
        },
      ]}
    />
  )
}

export default PatientDetails
